import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Box, Container, Typography, Button } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import JobOfferEdit from "./JobOfferEdit";
import sh from "bundles/common/utils/sh";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import wipImg from "pictures/wip.png";
import { trackJobOfferCreation } from "bundles/common/utils/marketing";
import { useBlocker } from "bundles/common/hooks/useBlocker";
import {
  uploadJobOfferMedia,
  attachJobOfferMedia,
} from "bundles/common/utils/uploadJobOfferMedia";

const JobOfferCreation = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const dispatch = useDispatch();
  const railsEnv = useSelector((state) => state.railsEnv);

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const queryJobOffer = queryParams.get("jobOffer");
  const draftMode = queryParams.get("draftMode") || false;

  const isEdit = queryJobOffer != null;

  const [jobOffer, setJobOffer] = useState(
    (currentRecruiter?.company?.job_offers || []).find(
      (offer) => offer.id == queryJobOffer
    ) || {}
  );
  const [skills, setSkills] = useState(jobOffer?.skills || []);

  const showWarning =
    !draftMode &&
    !jobOffer?.id &&
    currentRecruiter != null &&
    !currentRecruiter["active_licence_credit?"];

  const [isBeingSaved, setIsBeingSaved] = useState(false);
  // Prevents a second save/publish click from firing while the first is still
  // creating the offer and uploading staged media.
  const savingRef = useRef(false);

  // Upload files that were staged during creation, now that the offer has an id.
  // Runs before the redirect so the media is attached; failures are surfaced but
  // don't block the (already created) offer.
  const uploadStagedMedia = async (jobOfferId, staged) => {
    const progressKey = enqueueSnackbar(
      intl.formatMessage({ id: "job_offer_creation.media.uploading" }),
      { variant: "info", persist: true }
    );
    let failures = 0;
    for (const item of staged) {
      const attributeName =
        item.kind === "video" ? "job_offer_videos" : "job_offer_images";
      try {
        if (item.blobSignedId) {
          // Bytes already in S3 (uploaded in the background) — just attach.
          await attachJobOfferMedia(
            item.blobSignedId,
            jobOfferId,
            attributeName,
            authenticityToken
          );
        } else {
          // Upload never finished (still uploading or errored) — do it now.
          await uploadJobOfferMedia(
            item.file,
            jobOfferId,
            attributeName,
            authenticityToken
          );
        }
      } catch (err) {
        failures += 1;
      }
    }
    closeSnackbar(progressKey);
    if (failures > 0) {
      enqueueSnackbar(
        intl.formatMessage({ id: "job_offer_creation.media.upload_failed" }),
        { variant: "error" }
      );
    }
  };
  const [activeBlocker, setActiveBlocker] = useState(
    !showWarning && !isBeingSaved
  );

  const fetchJobOffer = () => {
    sh.get(`recruiter/job_offers/${queryJobOffer}`, {
      authenticity_token: authenticityToken,
    })
      .then((res) => {
        setJobOffer(res.data?.data);
        setSkills(res.data.data.skills);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleJobOfferSave = (withPublish = false) => {
    if (savingRef.current) return;
    savingRef.current = true;
    let job_offer = {
      description: jobOffer?.description,
      responsibilities: jobOffer?.responsibilities,
      profile: jobOffer?.profile,
      offer: jobOffer?.offer,
      offer_language: jobOffer?.offer_language,
      company_industries: jobOffer?.company_industries,
      categories: jobOffer?.categories,
      job_title: jobOffer?.job_title,
      job_statuses: jobOffer?.job_statuses,
      locations: jobOffer?.locations,
      monthly_offer: jobOffer?.monthly_offer,
      start_date: jobOffer?.start_date,
      end_date: jobOffer?.end_date,
      offer_status: jobOffer?.offer_status || "draft",
      offer_advantages: jobOffer?.offer_advantages,
      external_application: jobOffer.external_application,
      external_application_url: jobOffer.external_application_url,
      days_validity: parseInt(jobOffer.days_validity),
      abroad: jobOffer?.abroad,
    };

    if (jobOffer?.id) {
      sh.put(`job_offer/${jobOffer.id}`, {
        authenticity_token: authenticityToken,
        job_offer,
        skills,
        with_publish: withPublish,
      })
        .then((res) => {
          handleSuccessfulSave(res, true, withPublish);
        })
        .catch((err) => {
          handleFailedSave(err);
        });
    } else {
      sh.post("job_offer", {
        authenticity_token: authenticityToken,
        job_offer: job_offer,
        skills: skills,
        with_publish: withPublish,
      })
        .then(async (res) => {
          if (railsEnv === "production") trackJobOfferCreation();
          const newId = res.data?.result?.job_offer?.id;
          const staged = jobOffer?.staged_media || [];
          if (newId && staged.length > 0) {
            await uploadStagedMedia(newId, staged);
          }
          handleSuccessfulSave(res, true, withPublish);
        })
        .catch((err) => {
          handleFailedSave(err);
        });
    }
  };

  const showSnackbar = (message, variant, autoHideDuration) => {
    enqueueSnackbar(message, {
      variant: variant,
      autoHideDuration: autoHideDuration,
    });
  };

  const handleSuccessfulSave = (res, created, isPublishing) => {
    let status;
    if (res.data.result.publish) {
      if (res.data.result.publish_error) {
        const messageId = created
          ? "flash.success.job_offer_creation_without_publish"
          : "flash.success.job_offer_edition_without_publish";
        showSnackbar(
          intl.formatMessage({
            id: messageId,
          }),
          "warning",
          15000
        );

        status = "draft";
      } else {
        const messageId = created
          ? "flash.success.job_offer_creation_with_publish"
          : "flash.success.job_offer_edition_with_publish";
        showSnackbar(
          intl.formatMessage({
            id: messageId,
          }),
          "success",
          2000
        );
        updateCurrentRecruiter(res.data.recruiter);
        status = "online";
      }
    } else {
      const messageId = created
        ? "flash.success.job_offer_creation"
        : "flash.success.job_offer_edit";
      showSnackbar(
        intl.formatMessage({
          id: messageId,
        }),
        "success",
        2000
      );
      status = res.data.result.job_offer.offer_status;
    }

    if ((!jobOffer.status || jobOffer.status == "draft") && isPublishing) {
      status = "online";
    }

    setIsBeingSaved(true);
    setTimeout(() => {
      window.location.href = `${window.location.origin}/${i18nLocale}/recruiter?tab=jobs&jobs_filter=${status}`;
    }, 500);
  };

  const updateCurrentRecruiter = useCallback(
    (recruiter) => {
      dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: recruiter });
    },
    [dispatch]
  );

  const handleFailedSave = () => {
    // Allow the recruiter to retry after a failed save.
    savingRef.current = false;
    enqueueSnackbar(
      intl.formatMessage({
        id: "flash.unknown",
      }),
      { variant: "error" }
    );
  };

  useBlocker(() => {}, activeBlocker);

  useEffect(() => {
    if (queryJobOffer) {
      fetchJobOffer();
    }
  }, [queryJobOffer]);

  useEffect(() => {
    if (isBeingSaved) {
      setActiveBlocker(false);
    }
  }, [isBeingSaved]);
  return (
    <Box component="main" sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      {/* Page header */}
      <Box
        component="section"
        sx={{
          px: { xs: "16px", sm: "24px", lg: "32px" },
          pt: { xs: "32px", md: "48px" },
          pb: { xs: "20px", md: "28px" },
          background: "linear-gradient(to bottom, hsl(178, 30%, 97%), #fff)",
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Typography
            align="center"
            sx={{
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              fontSize: { xs: "1.5rem", md: "2.25rem" },
              color: "hsl(178, 93%, 10%)",
              letterSpacing: "-0.025em",
            }}
          >
            <FormattedMessage id="recruiter.create_new_job_title" />
          </Typography>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          px: { xs: "16px", sm: "24px", lg: "32px" },
          pb: { xs: "48px", md: "64px" },
        }}
      >
        {isEdit || !showWarning ? (
          <JobOfferEdit
            jobOffer={jobOffer}
            setJobOffer={setJobOffer}
            skills={skills}
            setSkills={setSkills}
            saveJobOffer={handleJobOfferSave}
            setActiveBlocker={setActiveBlocker}
          />
        ) : (
          <Box
            sx={{
              maxWidth: "520px",
              mx: "auto",
              mt: { xs: "16px", md: "32px" },
              p: { xs: "24px", md: "40px" },
              textAlign: "center",
              bgcolor: "#fff",
              border: "1px solid hsl(160, 10%, 90%)",
              borderRadius: "12px",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: "hsl(178, 93%, 10%)",
                mb: "24px",
              }}
            >
              <FormattedMessage id="credits_box.no_job_offer_credits" />
            </Typography>
            <Box
              component="img"
              src={wipImg}
              sx={{ maxWidth: "240px", width: "100%", mb: "24px" }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Button
                variant="rounded"
                color="secondaryOutlined"
                sx={{
                  width: "240px",
                  textTransform: "none",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "1rem",
                }}
                onClick={() =>
                  (window.location.href = `/${i18nLocale}/recruiter/pricing`)
                }
              >
                <FormattedMessage id="credits_box.add_credits" />
              </Button>
              <Button
                variant="rounded"
                color="secondaryContained"
                sx={{
                  width: "240px",
                  textTransform: "none",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "1rem",
                }}
                onClick={() =>
                  (window.location.href = `/${i18nLocale}/recruiter/job_offer_creation?draftMode=true`)
                }
              >
                <FormattedMessage id="job_offer.create_draft" />
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default JobOfferCreation;
